import { InfoIcon } from '@chakra-ui/icons';
import {
  FormControl,
  FormLabel,
  HStack,
  Icon,
  ListItem,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useEffect } from 'react';
import { FileRejection } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { FormErrors, noop } from '@blockpulse3/data/shared';
import {
  IndividualDocumentType,
  useDeleteKycDocumentMutation,
  useGetIndividualDocumentsQuery,
} from '@blockpulse3/graphql/hooks';
import {
  DropzoneInput,
  ErrorMessage,
  ErrorQueryModal,
  SkeletonModal,
} from '@blockpulse3/ui/commons';

import { useUser } from '../../../../../providers';
import { kycSchema } from '../schema';
import { IVerificationUserForm } from '../types';

type Props = {
  onSubmit: () => void;
  setIsValidable: React.Dispatch<React.SetStateAction<boolean>>;
};

export function DocumentUserVerification({ onSubmit = noop, setIsValidable }: Props): JSX.Element {
  const i18nSubscriptionIdentityVerificationModal = useTranslations(
    'SubscriptionIdentityVerificationModal',
  );
  const i18nDocumentVerification = useTranslations('DocumentVerification');
  const i18nTooltip = useTranslations('DocumentVerification.Tooltip');

  const { user } = useUser();
  const individualIdentityId = user?.individualIdentity?.id || '';

  /* ** Fetch individual documents ** */
  const individualDocumentsReq = useGetIndividualDocumentsQuery({
    variables: { individualIdentityId },
    skip: !individualIdentityId,
  });
  /* ** Delete document mutation ** */
  const [deleteDocument] = useDeleteKycDocumentMutation();

  /* ** Linked subscription individual documents to filter ** */
  const individualDocuments = individualDocumentsReq.data?.getIndividualDocuments || [];

  const identityDocument = individualDocuments.find(
    (doc) => doc.document.type === IndividualDocumentType.IDENTITY_DOCUMENT,
  );
  const identityDocumentSecondary = individualDocuments.find(
    (doc) => doc.document.type === IndividualDocumentType.IDENTITY_DOCUMENT_SECONDARY,
  );

  /* ** Documents file names for the `<DropzoneInput />` ** */
  const identityDocumentFileName = identityDocument
    ? {
        [identityDocument.document.id]: new File(
          [identityDocument.document.title],
          identityDocument.document.title,
        ),
      }
    : {};
  const identityDocumentSecondaryFileName = identityDocumentSecondary
    ? {
        [identityDocumentSecondary.document.id]: new File(
          [identityDocumentSecondary.document.title],
          identityDocumentSecondary.document.title,
        ),
      }
    : {};

  /* ** Form ** */
  const { control, handleSubmit, formState, setError, setValue, clearErrors } =
    useForm<IVerificationUserForm>({
      defaultValues: { identityDocumentFile: '', identityDocumentSecondaryFile: '' },
      resolver: yupResolver(kycSchema),
    });

  useEffect(() => {
    /* ** Set document in form on file change ** */
    if (identityDocument) {
      setValue('identityDocumentFile', identityDocument?.document.id || '');
    }
    if (identityDocumentSecondary) {
      setValue('identityDocumentSecondaryFile', identityDocumentSecondary?.document.id || '');
    }

    if (identityDocument && identityDocumentSecondary) {
      setIsValidable(true);
    } else {
      setIsValidable(false);
    }
  }, [identityDocument, identityDocumentSecondary, setValue, setIsValidable]);

  if (individualDocumentsReq.loading) {
    return <SkeletonModal h="380px" />;
  }

  if (individualDocumentsReq.error || individualDocumentsReq.error) {
    return <ErrorQueryModal h="380px" />;
  }

  if (!individualDocumentsReq.data) {
    return <ErrorQueryModal h="380px" />;
  }

  /* ** On Identity Document Secondary file upload ** */
  const handleIdentityDocumentSecondaryFileDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ): void => {
    clearErrors('identityDocumentSecondaryFile');
    if (fileRejections.length === 0) {
      handleFileUpload(acceptedFiles, IndividualDocumentType.IDENTITY_DOCUMENT_SECONDARY);
    } else {
      const error = fileRejections[0].errors[0].code;
      switch (error) {
        case 'file-too-large': {
          setError('identityDocumentSecondaryFile', {
            type: 'custom',
            message: FormErrors.FileTooLarge,
          });
          break;
        }
        default: {
          setError('identityDocumentSecondaryFile', {
            type: 'custom',
            message: FormErrors.DropzoneDefault,
          });
          break;
        }
      }
    }
  };

  /* ** On Identity Document file upload ** */
  const handleIdentityDocumentFileDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ): void => {
    clearErrors('identityDocumentFile');
    if (fileRejections.length === 0) {
      handleFileUpload(acceptedFiles, IndividualDocumentType.IDENTITY_DOCUMENT);
    } else {
      const error = fileRejections[0].errors[0].code;
      switch (error) {
        case 'file-too-large': {
          setError('identityDocumentFile', { type: 'custom', message: FormErrors.FileTooLarge });
          break;
        }
        default: {
          setError('identityDocumentFile', { type: 'custom', message: FormErrors.DropzoneDefault });
          break;
        }
      }
    }
  };

  /* ** Method to upload a file using axios ** */
  const handleFileUpload = async (
    acceptedFiles: File[],
    documentType: IndividualDocumentType,
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('document', acceptedFiles[0]);
    formData.append('individualIdentityId', individualIdentityId);
    formData.append('documentType', documentType);
    await axios.post(
      process.env['NX_API_CONTROLLER_ENDPOINT'] + '/identities/uploadKycDocument',
      formData,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    individualDocumentsReq.refetch();
  };

  const handleFormSubmit: SubmitHandler<IVerificationUserForm> = (): void => {
    onSubmit();
  };

  /* ** Delete file handlers ** */
  const handleIdentityDocumentDelete = (): void => {
    handleFileDelete(IndividualDocumentType.IDENTITY_DOCUMENT);
  };
  const handleIdentityDocumentSecondaryDelete = (): void => {
    handleFileDelete(IndividualDocumentType.IDENTITY_DOCUMENT_SECONDARY);
  };
  const handleFileDelete = (documentType: IndividualDocumentType): void => {
    deleteDocument({
      variables: {
        deleteKycDocumentInput: {
          individualIdentityId,
          documentType,
        },
      },
      onCompleted: () => {
        individualDocumentsReq.refetch();
      },
    });
  };

  return (
    <Stack py="4" spacing="2">
      <form id="document-upload" onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing="4">
          <HStack>
            <Text fontWeight="500">{i18nDocumentVerification('Title')}</Text>
            <Tooltip
              hasArrow
              placement="top"
              label={
                <UnorderedList>
                  <ListItem>{i18nTooltip('ListItem1')}</ListItem>
                  <ListItem>{i18nTooltip('ListItem2')}</ListItem>
                </UnorderedList>
              }
            >
              <Icon as={InfoIcon} color="gray.600" />
            </Tooltip>
          </HStack>
          <Controller
            control={control}
            name="identityDocumentFile"
            render={(): JSX.Element => (
              <FormControl>
                <FormLabel>{i18nDocumentVerification('IdentityDocument1')}</FormLabel>
                <DropzoneInput
                  accept={{ 'application/pdf': [], 'image/png': [], 'image/jpeg': [] }}
                  files={identityDocumentFileName}
                  maxFiles={1}
                  subTitle={i18nSubscriptionIdentityVerificationModal('UploadSubtitle')}
                  onDelete={handleIdentityDocumentDelete}
                  onDrop={handleIdentityDocumentFileDrop}
                />
                <ErrorMessage error={formState.errors?.identityDocumentFile} />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="identityDocumentSecondaryFile"
            render={(): JSX.Element => (
              <FormControl>
                <FormLabel>{i18nDocumentVerification('IdentityDocument2')}</FormLabel>
                <DropzoneInput
                  accept={{ 'application/pdf': [], 'image/png': [], 'image/jpeg': [] }}
                  files={identityDocumentSecondaryFileName}
                  maxFiles={1}
                  subTitle={i18nSubscriptionIdentityVerificationModal('UploadSubtitle')}
                  onDelete={handleIdentityDocumentSecondaryDelete}
                  onDrop={handleIdentityDocumentSecondaryFileDrop}
                />
                <ErrorMessage error={formState.errors?.identityDocumentSecondaryFile} />
              </FormControl>
            )}
          />
        </Stack>
      </form>
    </Stack>
  );
}
